import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  Tag
} from 'lucide-react';
import adminService from '../../api/services/adminService';

const COUPON_TYPES = ['percentage', 'fixed_amount', 'free_shipping'];
const CUSTOMER_TYPES = ['individual', 'parent', 'educator', 'school', 'homeschool', 'distributor'];

const EMPTY_FORM = {
  code: '',
  name: '',
  type: 'percentage',
  value: '',
  isActive: true,
  isAutomatic: false,
  maxUses: '',
  minimumOrderAmount: '',
  startDate: '',
  endDate: '',
  customerTypes: [],
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CouponsPage = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await adminService.getCoupons();
      const list = response.coupons || response.data || [];
      setCoupons(Array.isArray(list) ? list : []);
    } catch (err) {
      setError('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingCoupon(null);
    setFormData({ ...EMPTY_FORM, customerTypes: [] });
    setFormError('');
    setIsModalOpen(true);
  };

  const openEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code || '',
      name: coupon.name || '',
      type: coupon.type || 'percentage',
      value: coupon.value ?? '',
      isActive: coupon.isActive !== false,
      isAutomatic: coupon.isAutomatic || false,
      maxUses: coupon.maxUses ?? '',
      minimumOrderAmount: coupon.minimumOrderAmount ?? '',
      startDate: coupon.startDate ? new Date(coupon.startDate).toISOString().split('T')[0] : '',
      endDate: coupon.endDate ? new Date(coupon.endDate).toISOString().split('T')[0] : '',
      customerTypes: coupon.customerTypes?.length ? coupon.customerTypes : [],
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const toggleCustomerType = (type) => {
    setFormData(d => {
      if (type === 'all') return { ...d, customerTypes: [] };
      const without = (d.customerTypes || []).filter(t => t !== type);
      const next = (d.customerTypes || []).includes(type) ? without : [...without, type];
      return { ...d, customerTypes: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Client-side validation
    if (!formData.code.trim() && !formData.isAutomatic) {
      setFormError('Coupon code is required for non-automatic coupons.');
      return;
    }
    if (!formData.name.trim()) {
      setFormError('Display name is required.');
      return;
    }
    if (!formData.type) {
      setFormError('Coupon type is required.');
      return;
    }
    if (formData.type !== 'free_shipping' && (formData.value === '' || parseFloat(formData.value) <= 0)) {
      setFormError('A positive value is required for percentage or fixed amount coupons.');
      return;
    }
    if (formData.type === 'percentage' && parseFloat(formData.value) > 100) {
      setFormError('Percentage value cannot exceed 100%.');
      return;
    }
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      setFormError('Start date must be before end date.');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        value: formData.value !== '' ? parseFloat(formData.value) : 0,
        maxUses: formData.maxUses !== '' ? parseInt(formData.maxUses) : null,
        minimumOrderAmount: formData.minimumOrderAmount !== '' ? parseFloat(formData.minimumOrderAmount) : 0,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      };
      if (editingCoupon) {
        await adminService.updateCoupon(editingCoupon._id, payload);
      } else {
        await adminService.createCoupon(payload);
      }
      setIsModalOpen(false);
      fetchCoupons();
    } catch (err) {
      setFormError(err.response?.data?.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminService.deleteCoupon(id);
      setDeleteConfirm(null);
      showToast('Coupon has been deactivated (not deleted). It will no longer be usable by customers.');
      fetchCoupons();
    } catch (err) {
      setError('Failed to deactivate coupon');
    }
  };

  const typeLabel = (type) => ({ percentage: 'Percentage', fixed_amount: 'Fixed Amount', free_shipping: 'Free Shipping' }[type] || type);
  const valueDisplay = (coupon) => {
    if (coupon.type === 'percentage') return `${coupon.value}%`;
    if (coupon.type === 'free_shipping') return 'Free Ship';
    return `${coupon.value} off`;
  };

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-6 right-6 z-[60] max-w-sm px-5 py-3 rounded-xl shadow-lg border text-sm font-poppins animate-slide-in-up ${
          toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-start gap-2">
            {toast.type === 'success' ? (
              <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            ) : (
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            )}
            <span>{toast.msg}</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
          <p className="text-gray-500 mt-1">Manage discount codes and promotions</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm gap-2">
          <Plus className="w-4 h-4" />New Coupon
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-600 p-4 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" /></div>
        ) : coupons.length === 0 ? (
          <div className="py-12 text-center text-gray-400"><Tag className="w-10 h-10 mx-auto mb-2 opacity-30" />No coupons yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Code</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Value</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Uses</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Expires</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coupons.map(coupon => (
                  <tr key={coupon._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-mono font-semibold text-gray-900 text-sm">{coupon.code}</p>
                      {coupon.name && <p className="text-xs text-gray-400">{coupon.name}</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{typeLabel(coupon.type)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-primary-600">{valueDisplay(coupon)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {coupon.usedCount || 0}{coupon.maxUses ? ` / ${coupon.maxUses}` : ''}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(coupon.endDate)}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(coupon)} className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteConfirm(coupon._id)} className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Deactivate Coupon?</h3>
            <p className="text-gray-500 text-sm mb-6">This will deactivate the coupon so it can no longer be used. The record will be kept for reference.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">{editingCoupon ? 'Edit Coupon' : 'New Coupon'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4 flex-1">
              {formError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                  <input required value={formData.code} onChange={e => setFormData(d => ({ ...d, code: e.target.value.toUpperCase() }))} placeholder="SUMMER20" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-300 uppercase" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                  <input required value={formData.name} onChange={e => setFormData(d => ({ ...d, name: e.target.value }))} placeholder="Summer Sale 20% off" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select value={formData.type} onChange={e => setFormData(d => ({ ...d, type: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                    {COUPON_TYPES.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value {formData.type === 'percentage' ? '(%) *' : formData.type === 'fixed_amount' ? '(amount) *' : ''}
                  </label>
                  <input
                    type="number" step="0.01" min="0"
                    required={formData.type !== 'free_shipping'}
                    disabled={formData.type === 'free_shipping'}
                    value={formData.value}
                    onChange={e => setFormData(d => ({ ...d, value: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
                  <input type="number" min="1" value={formData.maxUses} onChange={e => setFormData(d => ({ ...d, maxUses: e.target.value }))} placeholder="Unlimited" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount</label>
                  <input type="number" step="0.01" min="0" value={formData.minimumOrderAmount} onChange={e => setFormData(d => ({ ...d, minimumOrderAmount: e.target.value }))} placeholder="No minimum" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={formData.startDate} onChange={e => setFormData(d => ({ ...d, startDate: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={formData.endDate} onChange={e => setFormData(d => ({ ...d, endDate: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>

              {/* Customer Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Types</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleCustomerType('all')}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      !(formData.customerTypes || []).length
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                    }`}
                  >
                    All
                  </button>
                  {CUSTOMER_TYPES.map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleCustomerType(type)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        (formData.customerTypes || []).includes(type)
                          ? 'bg-primary-500 text-white border-primary-500'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="couponActive" checked={formData.isActive} onChange={e => setFormData(d => ({ ...d, isActive: e.target.checked }))} className="rounded" />
                  <label htmlFor="couponActive" className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="couponAuto" checked={formData.isAutomatic} onChange={e => setFormData(d => ({ ...d, isAutomatic: e.target.checked }))} className="rounded" />
                  <label htmlFor="couponAuto" className="text-sm text-gray-700">Auto-apply</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={formLoading} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 disabled:opacity-60 flex items-center gap-2">
                  {formLoading && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                  {editingCoupon ? 'Save Changes' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponsPage;
